/**
 * profileConditions.js — ONE way to read `conditions`, and ONE way to write it.
 *
 * ★★★ THE DEFECT (TEST 1.03, found 2026-09-21 in the end-to-end dry run, step C05).
 *   ProfileScreen's Auto-fill import saved the extracted medical history like this:
 *
 *       const conditions = { ...profile?.medicalHistory?.conditions }
 *       data.medicalHistory.forEach(item => { if (item.condition) conditions[item.condition] = true })
 *
 *   `{ ...anArray }` DOES NOT PRODUCE AN ARRAY — it produces an object keyed by the
 *   indices ({...['a','b']} -> {"0":"a","1":"b"}) — and the loop then adds NAME keys on
 *   top. After one import the stored value is a mixture:
 *       {"0":{id,name},"1":{id,name},"Type 2 diabetes mellitus":true}
 *   Truthy, and not an array. `familyHistory.conditions` was written the same way, keyed
 *   `${condition}_${relation}`.
 *
 * ★★★ THE CRASH IS THE SMALLER HALF. Every reader in this app expects an ARRAY OF
 *   OBJECTS with .id/.name. Five readers died on the object shape and three survived by
 *   silently DISCARDING it — so even where the app stayed up, the imported conditions
 *   could not render, while the import reported success.
 *
 * ★★ THE GUARD SHAPE IS THE ONE buildProfileSummary ALREADY USED on its other half
 *   (`Array.isArray(fh.conditions)`), not a second one invented here. It is `isArray`
 *   below, and it is the first thing `toConditionList` asks. What this file adds on top
 *   is RECOVERY: the poison value is PERSISTED (sessionStorage on web, per authStorage),
 *   so a guard that answers `[]` would leave every already-poisoned profile permanently
 *   missing its conditions — which is exactly the silent drop the row was filed for.
 *   An object is therefore COERCED back to the canonical list, never dropped.
 *
 * THE CANONICAL SHAPES, from useOnboardingStore.js:
 *   medicalHistory.conditions : [{ id, name, year, custom? }]
 *   familyHistory.conditions  : [{ id, name, mother, father, sibling, grandparent }]
 *   familyHistory.otherConditions : same, for names outside the FAMILY_CONDITIONS catalog
 */
import { MEDICAL_CONDITIONS, FAMILY_CONDITIONS } from '../data/medicalLists.js'

/** The four relatives the family-history editor models. */
export const FAMILY_RELATIVES = ['mother', 'father', 'sibling', 'grandparent']

const isArray = Array.isArray
const isIndexKey = (k) => /^\d+$/.test(k)

export const conditionSlug = (s) => String(s || '')
  .trim().toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')

/** Map a free-text relation ("Mother", "maternal grandparent") onto a modelled flag. */
export function relativeFlag(relation) {
  const r = String(relation || '').toLowerCase()
  if (r.includes('grandparent') || r.includes('grandmother') || r.includes('grandfather')) return 'grandparent'
  if (r.includes('mother') || r.includes('mom')) return 'mother'
  if (r.includes('father') || r.includes('dad')) return 'father'
  if (r.includes('sibling') || r.includes('brother') || r.includes('sister')) return 'sibling'
  return null
}

/** One entry of any accepted shape -> a canonical condition object, or null. */
function entryToCondition(key, value) {
  let base = {}
  let name = ''
  if (value && typeof value === 'object') {
    base = { ...value }
    name = value.name || value.condition || value.nameKey || (isIndexKey(key) ? '' : key)
  } else if (value === true) {
    name = isIndexKey(key) ? '' : key
  } else if (typeof value === 'string' && value.trim()) {
    name = value
  } else {
    return null            // false / null / 0 / '' — not a recorded condition
  }
  if (!name) return null
  const out = { ...base, name }
  if (!out.id) out.id = `imported-${conditionSlug(name)}` || `imported-${conditionSlug(key)}`
  if (base.relation) {
    const flag = relativeFlag(base.relation)
    if (flag) out[flag] = true
  }
  return out
}

/**
 * THE READER. Any stored `conditions` value -> the canonical array of objects.
 * Accepts: an array (of objects OR of bare strings, the shape ProfileMedicalHistory
 * already coerced in place), the legacy index/name-keyed object the import wrote,
 * a single string, null and undefined.
 */
export function toConditionList(value) {
  if (isArray(value)) {
    return value.map((v, i) => entryToCondition(String(i), v)).filter(Boolean)
  }
  if (value && typeof value === 'object') {
    return Object.entries(value).map(([k, v]) => entryToCondition(k, v)).filter(Boolean)
  }
  if (typeof value === 'string' && value.trim()) {
    return [{ id: `imported-${conditionSlug(value)}`, name: value.trim(), custom: true }]
  }
  return []
}

/** Catalog lookup by name, so an imported name re-uses the id the editors expect. */
function fromCatalog(catalog, name) {
  const n = conditionSlug(name)
  return catalog.find((c) => conditionSlug(c.name) === n || conditionSlug(c.id) === n) || null
}

/**
 * THE WRITER, medical history. Existing value (any shape) + extracted condition names
 * -> the canonical array, de-duplicated by catalog id / slugged name.
 */
export function mergeImportedConditions(existing, names) {
  const out = toConditionList(existing)
  const seen = new Set(out.map((c) => conditionSlug(c.id)))
  for (const raw of names || []) {
    const name = String(raw && raw.condition ? raw.condition : (raw && raw.name) || raw || '').trim()
    if (!name) continue
    const hit = fromCatalog(MEDICAL_CONDITIONS, name)
    const item = hit
      ? { id: hit.id, name: hit.name, year: '' }
      : { id: `imported-${conditionSlug(name)}`, name, year: '', custom: true }
    if (seen.has(conditionSlug(item.id))) continue
    seen.add(conditionSlug(item.id))
    out.push(item)
  }
  return out
}

/**
 * THE WRITER, family history. Returns BOTH lists, because the family-history editor
 * rebuilds `conditions` from the FAMILY_CONDITIONS catalog and keeps anything outside it
 * in `otherConditions` — a name written into `conditions` with a minted id is dropped by
 * that rebuild, which would be the same silent loss one level along.
 */
export function mergeImportedFamilyConditions(existingConditions, existingOther, items) {
  const conditions = toConditionList(existingConditions)
  const other = toConditionList(existingOther)
  const put = (list, item) => {
    const i = list.findIndex((c) => conditionSlug(c.id) === conditionSlug(item.id))
    if (i >= 0) list[i] = { ...list[i], ...item }
    else list.push(item)
  }
  for (const raw of items || []) {
    const name = String((raw && (raw.condition || raw.name)) || raw || '').trim()
    if (!name) continue
    const flag = relativeFlag(raw && raw.relation)
    const hit = fromCatalog(FAMILY_CONDITIONS, name)
    const blank = { mother: false, father: false, sibling: false, grandparent: false }
    if (hit) {
      const prev = conditions.find((c) => conditionSlug(c.id) === conditionSlug(hit.id)) || {}
      put(conditions, { ...blank, ...prev, id: hit.id, name: hit.name, ...(flag ? { [flag]: true } : {}), relation: raw && raw.relation })
    } else {
      const id = `imported-${conditionSlug(name)}`
      const prev = other.find((c) => conditionSlug(c.id) === conditionSlug(id)) || {}
      put(other, { ...blank, ...prev, id, name, ...(flag ? { [flag]: true } : {}), relation: raw && raw.relation, custom: true })
    }
  }
  return { conditions, otherConditions: other }
}
